import { useState, useEffect } from "react";

type LessonSideBarProps = {
    lessonList: any[];
    onSelectLesson: (lessonId: number) => void;
    selectedLessonId?: number | null;
    selectedSubLesson?: number | null;
    onSelectSubLesson: (subLessonId: number, lessonId: number) => void;
    isLogin: boolean;
    isLearner: boolean
};

export const LessonsSideBar = ({
    lessonList,
    onSelectLesson,
    selectedLessonId: selectedLessonIdProp,
    selectedSubLesson: selectedSubLessonIdProp,
    onSelectSubLesson,
    isLogin,
    isLearner
}: LessonSideBarProps) => {
    const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);
    const [selectedSubLessonId, setSelectedSubLessonId] = useState<number | null>(null);
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        if (isSidebarOpen) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }

        return () => document.body.classList.remove("overflow-hidden");
    }, [isSidebarOpen]);

    useEffect(() => {
        if (selectedLessonIdProp) {
            setSelectedLessonId(selectedLessonIdProp);
        }
    }, [selectedLessonIdProp]);

    useEffect(() => {
        if (selectedSubLessonIdProp) {
            setSelectedSubLessonId(selectedSubLessonIdProp);
        }
    }, [selectedSubLessonIdProp]);

    const handleLessonClick = (lessonId: number) => {
        if (isLogin && isLearner) {
            setSelectedLessonId(lessonId);
            setSelectedSubLessonId(null);
            onSelectLesson(lessonId);
        }
    };

    const handleSubLessonClick = (subLessonId: number, lessonId: number) => {
        setSelectedSubLessonId(subLessonId);
        setSelectedLessonId(lessonId);
        onSelectSubLesson(subLessonId, lessonId);
    };

    return (
        <>
            <div className="md:h-[calc(100vh-200px)] thin-scrollbar overflow-y-auto overflow-x-hidden md:border-r border-[#D0D0D0] md:w-52 md:min-w-[13rem] md:max-w-[13rem] max-md:w-full flex-shrink-0 break-words">
                <div className="w-full p-2 border-b border-[#D0D0D0] max-md:hidden">
                    <span className="text-lg font-semibold  whitespace-nowrap ">Course Content</span>
                </div>
                <div className="w-full p-3 border-b border-[#D0D0D0] md:hidden flex justify-between items-center">
                    <span className="text-lg font-semibold">Course Content</span>

                    {/* Mobile Menu Button */}
                    <button onClick={() => setSidebarOpen(true)}>
                        <span className="material-symbols-outlined text-3xl">menu</span>
                    </button>
                </div>

                {lessonList?.map((lesson: any, index: number) => {
                    const isActive = selectedLessonId === lesson.id && !selectedSubLessonIdProp && isLogin && isLearner;
                    return (
                        <div key={lesson.id} className="max-md:hidden">
                            {/* Lesson Row */}
                            <div
                                className={`px-6 py-2 border-b border-[#D0D0D0] ${isActive
                                    ? "text-primary font-bold bg-gray-100"
                                    : isLogin && isLearner ? "text-black hover:text-primary hover:bg-gray-50"
                                        : "text-black"
                                    } ${isLogin && isLearner ? "cursor-pointer" : "cursor-default"}`}
                                onClick={() => handleLessonClick(lesson.id)}
                            >
                                <span className=" flex-1 text-sm font-semibold break-words whitespace-normal leading-snug">
                                    {`${index + 1}: ${lesson?.title}`}
                                </span>
                            </div>


                            {lesson.subLessons?.length > 0 && (
                                <div className="ml-8 border-l border-gray-200">
                                    {lesson.subLessons.map((sub: any, subIndex: number) => (
                                        <div
                                            key={sub.id}
                                            className={`px-4 py-1 cursor-pointer text-sm ${selectedSubLessonId === sub.sublessonId
                                                ? "text-primary font-semibold"
                                                : "text-gray-600 hover:text-primary"
                                                }`}
                                            onClick={() => handleSubLessonClick(sub.sublessonId, lesson.id)}
                                        >
                                            {`${index + 1}.${subIndex + 1} ${sub.subLessonTitle}`}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

            </div>

            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div className="fixed inset-0 z-[10000] flex">

                    {/* BACKDROP */}
                    <div
                        className="fixed inset-0 bg-black bg-opacity-40"
                        onClick={() => setSidebarOpen(false)}
                    />

                    {/* SLIDING DRAWER */}
                    <div className="relative z-[10001] w-72 bg-white h-screen shadow-xl animate-slideIn pb-12">

                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b">
                            <span className="text-lg font-semibold">Course Content</span>
                            <button onClick={() => setSidebarOpen(false)}>
                                <span className="material-symbols-outlined text-3xl">close</span>
                            </button>
                        </div>

                        {/* Lesson Items */}
                        <div className="overflow-y-auto max-md:h-[calc(100vh-200px)] p-2">
                            {lessonList?.map((lesson: any, index: number) => {
                                const isActive =
                                    selectedLessonId === lesson.id &&
                                    !selectedSubLessonIdProp &&
                                    isLogin &&
                                    isLearner;

                                return (
                                    <div key={lesson.id}>

                                        {/* Lesson Row */}
                                        <div
                                            className={`px-6 py-2 border-b border-[#D0D0D0] ${isActive
                                                ? "text-primary font-bold bg-gray-100 cursor-pointer"
                                                : isLogin ? "text-black hover:text-primary cursor-pointer hover:bg-gray-50"
                                                    : "text-black"
                                                }`}
                                            onClick={() => {
                                                setSidebarOpen(false);
                                                handleLessonClick(lesson.id)
                                            }}
                                        >
                                            {`${index + 1}: ${lesson.title}`}
                                        </div>

                                        {/* Sub Lessons */}
                                        {lesson.subLessons?.length > 0 && (
                                            <div className="ml-6 border-l border-gray-200">
                                                {lesson.subLessons.map((sub: any, subIndex: number) => (
                                                    <div
                                                        key={sub.id}
                                                        className={`px-4 py-1 cursor-pointer text-sm ${selectedSubLessonId === sub.sublessonId
                                                            ? "text-primary font-semibold"
                                                            : "text-gray-600 hover:text-primary"
                                                            }`}
                                                        onClick={() => {
                                                            setSidebarOpen(false);
                                                            handleSubLessonClick(sub.sublessonId, lesson.id)
                                                        }
                                                        }
                                                    >
                                                        {`${index + 1}.${subIndex + 1} ${sub.subLessonTitle}`}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

        </>

    );
};
